import { PageContent } from "@/shared/widgets/layout/PageContent";
import { useParams } from "react-router-dom";
import { CategoryBasicInfo } from "./CategoryBasicInfo";
import { CategoryFormActions } from "./CategoryFormActions";
import { useCategoryMutations } from "@/features/categories/hooks/useCategoryMutations";
import { useCategoryData } from "@/features/categories/hooks/useCategoriesData";
import { useState, useEffect } from "react";
import { toast } from "@/shared/hooks/use-toast";
import { useTranslation } from "@/shared/i18n";

const CategoryEdit = () => {
    const { t } = useTranslation('categories');
    const { id } = useParams<{ id: string }>();
    const isEditing = !!id;
    
    const { data: categoryData, isLoading: isLoadingCategory } = useCategoryData(id || "");
    const { createCategory, updateCategory } = useCategoryMutations();
    
    const [formData, setFormData] = useState({
        name: "",
        type: "",
    });

    useEffect(() => {
        if (isEditing && categoryData) {
            setFormData({
                name: categoryData.name,
                type: categoryData.type,
            });
        }
    }, [categoryData, isEditing]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.type) {
            toast({
                title: t('attention'),
                description: t('fillRequiredFields'),
                variant: "destructive",
            });
            return;
        }

        try {
            if (isEditing && id) {
                updateCategory.mutate({ 
                    id, 
                    data: { name: formData.name, type: formData.type } 
                });
            } else {
                createCategory.mutate({ 
                    name: formData.name, 
                    type: formData.type 
                });
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    const isSubmitting = createCategory.isPending || updateCategory.isPending;

    if (isEditing && isLoadingCategory) {
        return (
            <PageContent
                title={isEditing ? t('editCategory') : t('newCategory')}
                items={[
                    {
                        label: t('home'),
                        href: "/",
                    },
                    {
                        label: t('categories'),
                        href: "/categories",
                    },
                    {
                        label: isEditing ? t('edit') : t('new'),
                        href: isEditing ? `/categories/edit/${id}` : "/categories/new",
                    },
                ]}
            >
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
            </PageContent>
        );
    }

    return (
        <PageContent
            title={isEditing ? t('editCategory') : t('newCategory')}
            items={[
                {
                    label: t('home'),
                    href: "/",
                },
                {
                    label: t('categories'),
                    href: "/categories",
                },
                {
                    label: isEditing ? t('edit') : t('new'),
                    href: isEditing ? `/categories/edit/${id}` : "/categories/new",
                },
            ]}
        >
            <form onSubmit={handleSubmit} className="space-y-6">
                <CategoryBasicInfo
                    formData={formData}
                    setFormData={setFormData}
                />
                
                <CategoryFormActions
                    isEditing={isEditing}
                    isSubmitting={isSubmitting}
                />
            </form>
        </PageContent>
    );
};

export default CategoryEdit;